import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'

const NEW_RELIC_TIMEOUT_MS = 5000
const NEW_RELIC_CHECK_INTERVAL_MS = 100
const NEW_RELIC_MAX_ATTEMPTS =
  NEW_RELIC_TIMEOUT_MS / NEW_RELIC_CHECK_INTERVAL_MS

declare global {
  interface Window {
    newrelic?: {
      noticeError: (error: Error, extraData?: Record<string, any>) => void
    }
    __PINIA__?: {
      state?: {
        value?: {
          customer?: {
            customerProfile?: {
              customer_id?: string
            }
          }
        }
      }
    }
  }
}

interface NewRelicOptions {
  init: {
    distributed_tracing: { enabled: boolean }
    privacy: { cookies_enabled: boolean }
  }
  info: {
    beacon: string
    errorBeacon: string
    licenseKey: string
    applicationID: string
    sa: number
  }
  loader_config: {
    accountID: string
    trustKey: string
    agentID: string
    licenseKey: string
    applicationID: string
  }
}

const safeStringify = (obj: any): string => {
  const seen = new WeakSet()
  return JSON.stringify(
    obj,
    (_, value) => {
      if (typeof value === 'function') return '[Function]'
      if (value === window) return '[Window]'
      if (value === document) return '[Document]'
      if (value && typeof value === 'object') {
        if (seen.has(value)) return '[Circular]'
        seen.add(value)
      }
      return value
    },
    2
  )
}

const parseConsoleErrorArgs = (
  args: any[]
): {
  errorMessage: string | null
  errorObject: Error | null
  extraData: Record<string, any>
} => {
  let errorMessage: string | null = null
  let errorObject: Error | null = null
  let extraData: Record<string, any> = {}

  args.forEach((arg) => {
    if (arg instanceof Error) {
      errorObject = arg
      errorMessage = errorMessage ?? arg.message
      extraData.originalStack = arg.stack
    } else if (typeof arg === 'string') {
      errorMessage = arg
    } else if (typeof arg === 'object' && arg !== null) {
      if (arg.message) {
        errorMessage = arg.message
      }
      if ('status' in arg && typeof arg.status !== 'undefined') {
        extraData.status = arg.status
      }
      if ('statusCode' in arg && typeof arg.statusCode !== 'undefined') {
        extraData.statusCode = arg.statusCode
      }
      if ('url' in arg && typeof arg.url !== 'undefined') {
        extraData.url = arg.url
      }
      if ('code' in arg && typeof arg.code !== 'undefined') {
        extraData.code = arg.code
      }
      if ('name' in arg && typeof arg.name !== 'undefined') {
        extraData.errorName = arg.name
      }
      if ('cause' in arg && typeof arg.cause !== 'undefined') {
        extraData.cause = arg.cause
      }
      if ('description' in arg && typeof arg.description !== 'undefined') {
        extraData.description = arg.description
      }
      if ('originalError' in arg && arg.originalError) {
        extraData.originalErrorMessage = arg.originalError.message
        extraData.originalErrorStack = arg.originalError.stack
      }
      extraData.rawError = safeStringify(arg)
    } else if (typeof arg === 'number') {
      extraData.numericValue = arg
    } else if (typeof arg === 'boolean') {
      extraData.booleanValue = arg
    }
  })

  return { errorMessage, errorObject, extraData }
}

const buildExtraData = (args: any[]): Record<string, any> => {
  const extraData: Record<string, any> = {
    source: 'console.error',
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    referrer: document.referrer,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    arguments: args
      .map((arg) => {
        if (typeof arg === 'object' && arg !== null) {
          return '[Object]'
        }
        return String(arg)
      })
      .join(', ')
  }

  // Add customer ID if available
  try {
    const customerStore = window.__PINIA__?.state?.value?.customer
    if (customerStore?.customerProfile?.customer_id) {
      extraData.customer_id = customerStore.customerProfile.customer_id
    }
  } catch (e) {
    console.debug('error getting customer data for error logging', e)
  }

  // Add performance metrics if available
  if (performance?.getEntriesByType) {
    const nav = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming
    if (nav) {
      extraData.pageLoadTime = nav.loadEventEnd - nav.startTime
      extraData.domReadyTime = nav.domContentLoadedEventEnd - nav.startTime
    }
  }

  return extraData
}

// Console error interceptor to automatically log all console.error calls to New Relic
const setupConsoleErrorInterceptor = () => {
  if (typeof window === 'undefined') {
    console.error('window is undefined, cannot setup console error interceptor')
    return
  }

  // Store reference to original console.error before any potential overrides
  const originalConsoleError = console.error

  // Only check for overrides in production
  if (import.meta.env.VITE_APP_DEV_MODE !== 'ON') {
    // Check if console.error has already been overridden by other libraries
    if (
      originalConsoleError.toString().includes('setupConsoleErrorInterceptor')
    ) {
      console.warn(
        'Console.error appears to be already overridden, skipping New Relic interceptor setup'
      )
      return
    }
  }

  console.error = (...args) => {
    originalConsoleError.apply(console, args)

    try {
      // Skip NR: already sent by Vue error handler with richer context
      if (
        args.length >= 2 &&
        args[0] === 'Vue Error:' &&
        args[1] instanceof Error
      ) {
        return true
      }

      const {
        errorMessage,
        errorObject,
        extraData: parsedExtraData
      } = parseConsoleErrorArgs(args)

      // Create error object if none exists
      const finalErrorObject =
        errorObject || new Error(errorMessage ?? 'Console Error')
      if (!errorObject) {
        finalErrorObject.stack = new Error().stack
      }

      // Build comprehensive extra data
      const finalExtraData = {
        ...parsedExtraData,
        ...buildExtraData(args)
      }

      if (
        errorMessage === 'Script error.' ||
        errorMessage === 'Script error' ||
        finalErrorObject.message === 'Script error.' ||
        finalErrorObject.message === 'Script error'
      ) {
        finalExtraData.isThirdPartyScriptError = true
        finalExtraData.filtered = true
      }

      console.debug('logging to new relic', finalErrorObject, finalExtraData)

      // Log to New Relic if available
      if (window.newrelic?.noticeError) {
        window.newrelic.noticeError(finalErrorObject, finalExtraData)
        return true
      } else {
        originalConsoleError(
          'New Relic not available for error logging:',
          finalErrorObject
        )
        return false
      }
    } catch (e) {
      originalConsoleError('Failed to log to New Relic:', e)
      return false
    }
  }
}

const setupGlobalErrorHandlers = (): void => {
  if (typeof window === 'undefined') return

  window.onerror = (message, source, lineno, colno, error) => {
    if (message === 'Script error.' && !source) {
      return true
    }
    console.error(error ?? message)
    return false
  }

  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled Promise Rejection:', event.reason)
  })
}

export const loadNewRelic = async (): Promise<void> => {
  if (import.meta.env.VITE_APP_DEV_MODE === 'ON') {
    console.log(`%c New Relic is disabled in dev mode`, 'color: orange;')
    return
  }

  const options: NewRelicOptions = {
    init: {
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true }
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey: `${import.meta.env.VITE_NEW_RELIC_LICENSE_KEY}`,
      applicationID: `${import.meta.env.VITE_NEW_RELIC_APPLICATION_ID}`,
      sa: 1
    },
    loader_config: {
      accountID: `${import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID}`,
      trustKey: `${import.meta.env.VITE_NEW_RELIC_TRUST_KEY}`,
      agentID: `${import.meta.env.VITE_NEW_RELIC_AGENT_ID}`,
      licenseKey: `${import.meta.env.VITE_NEW_RELIC_LICENSE_KEY}`,
      applicationID: `${import.meta.env.VITE_NEW_RELIC_APPLICATION_ID}`
    }
  }

  new BrowserAgent(options)

  // Wait for New Relic to be available before setting up the interceptor
  const waitForNewRelic = () => {
    return new Promise<void>((resolve) => {
      let attempts = 0

      const checkNewRelic = () => {
        attempts++
        if (window.newrelic?.noticeError) {
          setupConsoleErrorInterceptor()
          resolve()
        } else if (attempts >= NEW_RELIC_MAX_ATTEMPTS) {
          console.warn(
            `New Relic failed to load within ${NEW_RELIC_TIMEOUT_MS}ms timeout, setting up interceptor anyway`
          )
          setupConsoleErrorInterceptor()
          resolve()
        } else {
          setTimeout(checkNewRelic, NEW_RELIC_CHECK_INTERVAL_MS)
        }
      }
      checkNewRelic()
    })
  }

  await waitForNewRelic()
  setupGlobalErrorHandlers()
}
